import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  User,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
  applyActionCode,
  ActionCodeSettings
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import toast from 'react-hot-toast';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  checkEmailVerified: () => Promise<boolean>;
  verifyActionCode: (code: string) => Promise<void>;
}

interface AuthAttempt {
  timestamp: number;
  success: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const authAttemptsRef = useRef<AuthAttempt[]>([]);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const MAX_RETRIES = 3;
  const MAX_ATTEMPTS_WINDOW = 5 * 60 * 1000; // 5 minutes
  const MAX_ATTEMPTS = 5;
  const COOLDOWN_PERIOD = 15 * 60 * 1000; // 15 minutes

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      setRetryCount(0);
      if (user) {
        authAttemptsRef.current = [];
        if (cooldownTimerRef.current) {
          clearTimeout(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      }
    });

    return () => {
      unsubscribe();
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  const checkRateLimit = (): boolean => {
    const now = Date.now();
    authAttemptsRef.current = authAttemptsRef.current.filter(
      attempt => now - attempt.timestamp < MAX_ATTEMPTS_WINDOW
    );

    if (authAttemptsRef.current.length >= MAX_ATTEMPTS) {
      const oldestAttempt = authAttemptsRef.current[0];
      const timeUntilReset = (oldestAttempt.timestamp + MAX_ATTEMPTS_WINDOW) - now;
      
      if (timeUntilReset > 0) {
        const minutes = Math.ceil(timeUntilReset / 60000);
        toast.error(`Trop de tentatives. Veuillez réessayer dans ${minutes} minute${minutes > 1 ? 's' : ''}.`);
        
        if (!cooldownTimerRef.current) {
          cooldownTimerRef.current = setTimeout(() => {
            authAttemptsRef.current = [];
            cooldownTimerRef.current = null;
            toast.success('Vous pouvez réessayer maintenant.');
          }, COOLDOWN_PERIOD);
        }
        
        return false;
      }
    }
    return true;
  };

  const recordAuthAttempt = (success: boolean) => {
    const now = Date.now();
    authAttemptsRef.current.push({ timestamp: now, success });
  };

  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      return false;
    }
  };

  const handleAuthError = async (error: any, operation: string, email?: string) => {
    recordAuthAttempt(false);

    if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found') {
      if (email) {
        const exists = await checkEmailExists(email);
        if (!exists) {
          toast.error('Aucun compte trouvé avec cet email. Veuillez créer un compte.');
          return 'signup';
        }
      }
      if (retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        toast.error('Email ou mot de passe incorrect');
        return 'retry';
      } else {
        toast.error('Trop de tentatives échouées. Veuillez réessayer plus tard.');
        return 'blocked';
      }
    } else if (error.code === 'auth/wrong-password') {
      toast.error('Mot de passe incorrect');
      return 'retry';
    } else if (error.code === 'auth/network-request-failed') {
      toast.error('Erreur de connexion. Vérifiez votre connexion internet.');
      return 'retry';
    } else if (error.code === 'auth/too-many-requests') {
      if (!cooldownTimerRef.current) {
        cooldownTimerRef.current = setTimeout(() => {
          authAttemptsRef.current = [];
          cooldownTimerRef.current = null;
          toast.success('Vous pouvez réessayer maintenant.');
        }, COOLDOWN_PERIOD);
      }
      toast.error('Trop de tentatives. Veuillez réessayer dans 15 minutes.');
      return 'blocked';
    } else if (error.code === 'auth/popup-closed-by-user') {
      return 'cancelled';
    } else {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
      return 'error';
    }
  };

  const sendVerificationEmail = async () => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    const actionCodeSettings: ActionCodeSettings = {
      url: window.location.origin + window.location.pathname,
      handleCodeInApp: true
    };

    try {
      await sendEmailVerification(currentUser, actionCodeSettings);
    } catch (error: any) {
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Trop de tentatives. Veuillez réessayer plus tard.');
      }
      throw error;
    }
  };

  const verifyActionCode = async (code: string) => {
    try {
      await applyActionCode(auth, code);
      if (currentUser) {
        await currentUser.reload();
        toast.success('Email vérifié avec succès !');
      }
    } catch (error) {
      toast.error('Le lien de vérification est invalide ou a expiré');
      throw error;
    }
  };

  const checkEmailVerified = async (): Promise<boolean> => {
    if (!currentUser) return false;
    await currentUser.reload();
    return currentUser.emailVerified;
  };

  const signUp = async (email: string, password: string) => {
    if (!checkRateLimit()) return;

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const displayName = email.split('@')[0];
      
      if (result.user) {
        await updateProfile(result.user, { displayName });
        
        const actionCodeSettings: ActionCodeSettings = {
          url: window.location.origin + window.location.pathname,
          handleCodeInApp: true
        };
        
        await sendEmailVerification(result.user, actionCodeSettings);
      }
      
      recordAuthAttempt(true);
      toast.success('Veuillez vérifier votre email pour activer votre compte');
      
      await signOut(auth);
      
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error('Un compte existe déjà avec cet email');
      } else {
        await handleAuthError(error, 'Sign up');
      }
      throw error;
    }
  };

  const signIn = async (email: string, password: string, rememberMe = false) => {
    if (!checkRateLimit()) return;

    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      if (!result.user.emailVerified) {
        await signOut(auth);
        throw new Error('EMAIL_NOT_VERIFIED');
      }
      
      recordAuthAttempt(true);
      toast.success('Connexion réussie !');
    } catch (error: any) {
      if (error.message === 'EMAIL_NOT_VERIFIED') {
        toast.error('Veuillez vérifier votre email avant de vous connecter');
        throw error;
      }
      
      const result = await handleAuthError(error, 'Sign in', email);
      if (result === 'retry') {
        return signIn(email, password, rememberMe);
      } else if (result === 'signup') {
        throw new Error('SIGNUP_REQUIRED');
      }
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    if (!checkRateLimit()) return;

    try {
      const testPopup = window.open('', '_blank', 'width=1,height=1');
      if (!testPopup || testPopup.closed || typeof testPopup.closed === 'undefined') {
        toast.error('Les popups sont bloqués. Veuillez les autoriser pour continuer.');
        return;
      }
      testPopup.close();

      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);
      
      if (result.user && !result.user.displayName) {
        const displayName = result.user.email?.split('@')[0] || 'Utilisateur';
        await updateProfile(result.user, { displayName });
      }
      
      recordAuthAttempt(true);
      toast.success('Connexion avec Google réussie !');
      return result;
    } catch (error: any) {
      const result = await handleAuthError(error, 'Google sign in');
      if (result === 'retry') {
        await signOut(auth);
        return signInWithGoogle();
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      toast.success('Déconnexion réussie');
      setRetryCount(0);
      authAttemptsRef.current = [];
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    } catch (error) {
      await handleAuthError(error, 'Logout');
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    sendVerificationEmail,
    checkEmailVerified,
    verifyActionCode
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};